import React, { useState, useEffect } from "react";
import axios from "axios";

const PORTAL_CONFIG = {
  admin: {
    icon: "🛡️",
    label: "Admin Portal",
    subtitle: "Full Management Access",
    accentFrom: "from-purple-600",
    accentTo: "to-indigo-600",
    ringColor: "focus:ring-purple-500/30 focus:border-purple-500",
    badgeColor: "bg-purple-100 text-purple-700",
    placeholder: "admin",
    forgotAccent: "from-purple-500 to-indigo-600",
    forgotRing: "focus:ring-purple-500/30 focus:border-purple-500",
  },
  staff: {
    icon: "👷",
    label: "Staff Portal",
    subtitle: "Appointment Management Access",
    accentFrom: "from-teal-500",
    accentTo: "to-emerald-600",
    ringColor: "focus:ring-teal-500/30 focus:border-teal-500",
    badgeColor: "bg-teal-100 text-teal-700",
    placeholder: "staff",
    forgotAccent: "from-teal-500 to-emerald-600",
    forgotRing: "focus:ring-teal-500/30 focus:border-teal-500",
  },
};

// ─── Admin / Staff Forgot Password Modal ─────────────────────────────────────
const AdminForgotPasswordModal = ({ onClose, portalType }) => {
  const portal = PORTAL_CONFIG[portalType] || PORTAL_CONFIG.admin;
  const [step, setStep] = useState(1); // 1=email, 2=otp, 3=newpw, 4=success
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  const startCooldown = () => {
    setResendCooldown(60);
    const t = setInterval(() => {
      setResendCooldown((c) => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; });
    }, 1000);
  };

  const sendOtp = async () => {
    setLoading(true); setError("");
    try {
      await axios.post("/api/auth/forgot-password", { email });
      setStep(2);
      startCooldown();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to send code.");
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setLoading(true); setError("");
    try {
      await axios.post("/api/auth/verify-otp", { email, otp });
      setStep(3);
    } catch (e) {
      setError(e.response?.data?.message || "Invalid code.");
    } finally { setLoading(false); }
  };

  const resetPassword = async () => {
    if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
    if (newPassword.length < 6) { setError("Password must be at least 6 characters."); return; }
    setLoading(true); setError("");
    try {
      await axios.post("/api/auth/reset-password", { email, otp, newPassword });
      setStep(4);
    } catch (e) {
      setError(e.response?.data?.message || "Reset failed.");
    } finally { setLoading(false); }
  };

  const steps = ["Email", "Verify Code", "New Password", "Done"];
  const inputCls = `w-full px-5 py-4 border border-slate-600 rounded-2xl bg-slate-800 text-white placeholder-slate-500 font-medium transition-all outline-none ${portal.forgotRing} focus:ring-4`;

  return (
    <div
      className="fixed inset-0 bg-black/80 backdrop-blur-md z-[70] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-slate-900 rounded-3xl shadow-2xl w-full max-w-md p-8 border border-slate-700 relative"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2.5 bg-slate-800 hover:bg-slate-700 rounded-xl text-slate-400 hover:text-white transition-all"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-7">
          <div className={`w-16 h-16 bg-gradient-to-r ${portal.forgotAccent} rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-xl`}>
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-white mb-1">Reset Password</h2>
          <p className="text-slate-400 text-sm">{portal.label}</p>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center justify-center mb-6 gap-1">
          {steps.map((s, i) => (
            <React.Fragment key={i}>
              <div
                className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold transition-all ${
                  step > i + 1
                    ? "bg-green-500 text-white"
                    : step === i + 1
                    ? `bg-gradient-to-r ${portal.forgotAccent} text-white shadow-md`
                    : "bg-slate-700 text-slate-400"
                }`}
              >
                {step > i + 1 ? "✓" : i + 1}
              </div>
              {i < steps.length - 1 && (
                <div className={`flex-1 h-0.5 max-w-[32px] rounded ${step > i + 1 ? "bg-green-400" : "bg-slate-700"}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm font-medium flex items-center gap-2">
            <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            {error}
          </div>
        )}

        {/* Step 1: Email */}
        {step === 1 && (
          <div className="space-y-4 animate-in fade-in duration-300">
            <p className="text-slate-400 text-sm text-center">
              Enter your registered email address and we'll send you a 6-digit reset code.
            </p>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Email Address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setError(""); }}
                className={inputCls}
                placeholder="your@email.com"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && email && sendOtp()}
              />
            </div>
            <button
              onClick={sendOtp}
              disabled={!email || loading}
              className={`w-full py-4 bg-gradient-to-r ${portal.forgotAccent} text-white font-bold text-base rounded-2xl shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
            >
              {loading ? "Sending..." : "Send Reset Code"}
            </button>
          </div>
        )}

        {/* Step 2: OTP */}
        {step === 2 && (
          <div className="space-y-4 animate-in fade-in duration-300">
            <p className="text-slate-400 text-sm text-center">
              We sent a 6-digit code to{" "}
              <span className="font-semibold text-white">{email}</span>. Check your inbox.
            </p>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Verification Code</label>
              <input
                type="text"
                value={otp}
                onChange={(e) => { setOtp(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
                className={`${inputCls} text-center text-2xl font-bold tracking-[0.5em]`}
                placeholder="------"
                maxLength={6}
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && otp.length === 6 && verifyOtp()}
              />
            </div>
            <button
              onClick={verifyOtp}
              disabled={otp.length !== 6 || loading}
              className={`w-full py-4 bg-gradient-to-r ${portal.forgotAccent} text-white font-bold text-base rounded-2xl shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
            >
              {loading ? "Verifying..." : "Verify Code"}
            </button>
            <div className="text-center">
              {resendCooldown > 0 ? (
                <p className="text-sm text-slate-500">
                  Resend in <span className="font-semibold text-slate-300">{resendCooldown}s</span>
                </p>
              ) : (
                <button
                  onClick={sendOtp}
                  disabled={loading}
                  className="text-sm font-semibold text-slate-400 hover:text-white transition-colors hover:underline"
                >
                  Resend Code
                </button>
              )}
            </div>
          </div>
        )}

        {/* Step 3: New Password */}
        {step === 3 && (
          <div className="space-y-4 animate-in fade-in duration-300">
            <p className="text-slate-400 text-sm text-center">Create a strong new password (min. 6 characters).</p>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">New Password</label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); setError(""); }}
                  className={`${inputCls} pr-12`}
                  placeholder="New password"
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showPw ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Confirm Password</label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(""); }}
                  className={`${inputCls} pr-12`}
                  placeholder="Confirm password"
                  onKeyDown={(e) => e.key === "Enter" && newPassword && confirmPassword && resetPassword()}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                >
                  {showConfirm ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            {newPassword && confirmPassword && newPassword === confirmPassword && (
              <p className="text-green-400 text-xs font-medium flex items-center gap-1">✓ Passwords match</p>
            )}
            <button
              onClick={resetPassword}
              disabled={!newPassword || !confirmPassword || loading}
              className={`w-full py-4 bg-gradient-to-r ${portal.forgotAccent} text-white font-bold text-base rounded-2xl shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
            >
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </div>
        )}

        {/* Step 4: Success */}
        {step === 4 && (
          <div className="text-center space-y-4 py-4 animate-in zoom-in-95 duration-300">
            <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full mx-auto flex items-center justify-center shadow-lg shadow-green-900/30">
              <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-white">Password Reset!</h3>
            <p className="text-slate-400 text-sm">
              Your password has been successfully updated. You can now log in with your new password.
            </p>
            <button
              onClick={onClose}
              className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold text-base rounded-2xl shadow-lg hover:scale-[1.02] transition-all"
            >
              Back to Login
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Admin Login Modal ───────────────────────────────────────────────────
const AdminLogin = ({ isOpen, onClose, setUser, setActiveTab }) => {
  const [step, setStep] = useState(1);
  const [adminPin, setAdminPin] = useState("");
  const [portalType, setPortalType] = useState(null); // "admin" | "staff"
  const [formData, setFormData] = useState({ emailOrPhone: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForgot, setShowForgot] = useState(false);

  const portal = portalType ? PORTAL_CONFIG[portalType] : null;

  // Auto-hide password and reset state whenever modal is opened or closed
  useEffect(() => {
    if (isOpen) {
      setShowPassword(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    setStep(1);
    setAdminPin("");
    setPortalType(null);
    setFormData({ emailOrPhone: "", password: "" });
    setShowPassword(false);
    setError("");
    setShowForgot(false);
    onClose();
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    if (adminPin.length < 4) {
      setError("PIN must be at least 4 digits");
      return;
    }

    setLoading(true);
    try {
      const cleanPin = adminPin.trim();
      const response = await axios.post("/api/auth/verify-pin", { adminPin: cleanPin });
      if (response.data.success) {
        setPortalType(response.data.portalType); // "admin" or "staff"
        setAdminPin(cleanPin);
        setStep(2);
        setError("");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Invalid PIN");
      setAdminPin("");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.emailOrPhone || !formData.password) {
      setError("Please fill all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post("/api/auth/login", {
        ...formData,
        isAdminLogin: true,
        adminPin: adminPin.trim(),
      });

      if (response.data.success) {
        localStorage.setItem("user", JSON.stringify(response.data));
        setUser(response.data);
        handleClose();
        setActiveTab("admin");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Login failed. Check your credentials.");
      if (err.response?.status === 401 && err.response?.data?.message?.includes("PIN")) {
        setStep(1);
        setAdminPin("");
        setPortalType(null);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <div
          className="bg-slate-900 rounded-3xl shadow-2xl w-full max-w-lg p-8 border border-slate-700 relative mx-4 max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-6 right-6 p-3 bg-slate-800 hover:bg-slate-700 rounded-2xl shadow-md transition-all hover:scale-110 text-slate-400 hover:text-white"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Header */}
          <div className="text-center mb-8 pt-4">
            <img
              src="/furpect-tails-logo.png"
              alt="Furpect Tails Logo"
              className="w-16 h-16 object-contain mx-auto mb-3 drop-shadow-md"
            />
            <div
              className={`w-16 h-16 bg-gradient-to-r ${portal ? portal.accentFrom : "from-slate-600"} ${portal ? portal.accentTo : "to-slate-700"} rounded-2xl mx-auto mb-4 shadow-xl border-4 border-slate-800 flex items-center justify-center text-3xl transition-all duration-500`}
            >
              {step === 1 ? "🔒" : portal?.icon}
            </div>
            <h2 className="text-3xl font-bold text-white mb-3 tracking-wide transition-all duration-300">
              {step === 1 ? "Security Check" : portal?.label}
            </h2>
            <p className="text-slate-400 text-sm font-medium uppercase tracking-widest">
              {step === 1 ? "Enter your PIN to continue" : portal?.subtitle}
            </p>

            {/* Portal Badge — only shown on step 2 */}
            {step === 2 && portal && (
              <div className={`inline-flex items-center gap-2 mt-4 px-4 py-1.5 rounded-full text-xs font-bold ${portal.badgeColor} animate-in zoom-in-95 duration-300`}>
                <span>{portal.icon}</span>
                <span>{portal.label}</span>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border-2 border-red-500/30 rounded-2xl text-red-400 text-sm font-bold flex items-center space-x-2 animate-in slide-in-from-top-2">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {/* Step 1: PIN Form */}
          {step === 1 && (
            <form onSubmit={handlePinSubmit} className="space-y-6 animate-in fade-in zoom-in duration-300">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-center">
                  Master PIN
                </label>
                <input
                  type="password"
                  value={adminPin}
                  onChange={(e) => { setAdminPin(e.target.value); setError(""); }}
                  className="w-full px-6 py-5 border border-slate-700 rounded-2xl focus:ring-4 focus:ring-purple-500/30 focus:border-purple-500 bg-slate-800 text-white text-3xl tracking-[1em] text-center font-black transition-all"
                  placeholder="••••"
                  maxLength={6}
                  autoFocus
                  required
                />
                <p className="text-center text-slate-500 text-xs mt-3">
                  Use Admin PIN for full access · Staff PIN for appointments only
                </p>
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full py-5 px-8 bg-slate-800 hover:bg-slate-700 text-white font-bold text-lg rounded-2xl shadow-xl transition-all duration-300 uppercase tracking-wider border border-slate-700 disabled:opacity-50"
              >
                {loading ? "Verifying..." : "Verify PIN"}
              </button>
            </form>
          )}

          {/* Step 2: Login Form */}
          {step === 2 && portal && (
            <form onSubmit={handleSubmit} className="space-y-6 animate-in slide-in-from-right-8 fade-in duration-300">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                  Username or Email
                </label>
                <div className="relative">
                  <input
                    type="text"
                    name="emailOrPhone"
                    value={formData.emailOrPhone}
                    onChange={handleInputChange}
                    className={`w-full px-14 py-4 border border-slate-700 rounded-2xl focus:ring-4 ${portal.ringColor} bg-slate-800 text-white text-lg placeholder-slate-500 font-medium transition-all`}
                    placeholder={portal.placeholder}
                    required
                    autoFocus
                  />
                  <svg className="absolute left-5 top-1/2 -translate-y-1/2 w-6 h-6 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-5 py-4 pr-16 border border-slate-700 rounded-2xl focus:ring-4 ${portal.ringColor} bg-slate-800 text-white text-lg placeholder-slate-500 font-medium transition-all`}
                    placeholder="Enter password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-5 top-1/2 -translate-y-1/2 p-2 hover:bg-slate-700 rounded-xl transition-all"
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {/* Forgot Password Link */}
                <div className="text-right mt-2">
                  <button
                    type="button"
                    onClick={() => setShowForgot(true)}
                    className="text-xs font-semibold text-slate-400 hover:text-white transition-colors hover:underline"
                  >
                    Forgot Password?
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-5 px-8 bg-gradient-to-r ${portal.accentFrom} ${portal.accentTo} text-white font-bold text-lg rounded-2xl shadow-xl hover:scale-[1.02] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed uppercase tracking-wider`}
              >
                {loading ? "Authenticating..." : `Access ${portal.label}`}
              </button>
              <div className="text-center mt-4">
                <button
                  type="button"
                  onClick={() => { setStep(1); setPortalType(null); setShowPassword(false); setError(""); }}
                  className="text-slate-500 text-xs uppercase tracking-widest hover:text-white transition-colors"
                >
                  ← Back to PIN
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* Admin / Staff Forgot Password Modal */}
      {showForgot && (
        <AdminForgotPasswordModal
          onClose={() => setShowForgot(false)}
          portalType={portalType}
        />
      )}
    </>
  );
};

export default AdminLogin;
